import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  InputNumber,
  Row,
  Col,
  Spin,
  Button,
  Collapse,
  Form,
  Input,
  DatePicker,
  Tooltip,
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import {
  LoadingOutlined,
  DownOutlined,
  UpOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import newAlert from 'src/components/newAlert';
import { start_loading, end_loading } from 'src/core/actions/loader';
import { roundFloat, replaceFloat } from 'src/core/utils/formater';
import { enable_state, disable_state } from 'src/core/actions/route_state';
import { customConfirm } from 'src/components/common/confirmModal';
import debounce from 'lodash.debounce';

const { Panel } = Collapse;

const EditableCellAddRow = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm(); // Use Form.useForm() for better handling
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus(); // Ensure input is focused
    }
  }, [editing]);

  const toggleEdit = () => {
    if (editable) {
      setEditing(!editing);
      console.log('Editing toggled for:', dataIndex);
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields(); // Use form.validateFields()
      setEditing(false);
      handleSave({ ...record, [dataIndex]: values[dataIndex] }); // Call with updated values
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form
        form={form}
        component={false}
        onFinish={save}
        initialValues={{ [dataIndex]: record[dataIndex] }} // Set initial values
      >
        <Form.Item
          style={{ margin: 0 }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
          ]}
        >
          {dataIndex === 'turul' ? (
            <Input
              ref={inputRef}
              onPressEnter={save}
              onBlur={save}
              style={{ width: '100%' }}
            />
          ) : (
            <InputNumber
              className="wide-input-number" // Apply the custom class
              ref={inputRef}
              min={0}
              step={0.01}
              onPressEnter={save}
              onBlur={save}
              style={{ width: '100%' }}
              formatter={(value) => `${value}`}
              parser={(value) => value}
              precision={2}
            />
          )}
        </Form.Item>
      </Form>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
      >
        {children || ' '}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableCellAddRow2 = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave3,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm(); // Use Form.useForm() for better handling
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus(); // Ensure input is focused
    }
  }, [editing]);

  const toggleEdit = () => {
    if (editable) {
      setEditing(!editing);
      console.log('Editing toggled for:', dataIndex);
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields(); // Use form.validateFields()
      setEditing(false);
      handleSave3({ ...record, [dataIndex]: values[dataIndex] }); // Call with updated values
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form
        form={form}
        component={false}
        onFinish={save}
        initialValues={{ [dataIndex]: record[dataIndex] }} // Set initial values
      >
        <Form.Item
          style={{ margin: 0 }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
          ]}
        >
          {dataIndex === 'turul' ? (
            <Input
              ref={inputRef}
              onPressEnter={save}
              onBlur={save}
              style={{ width: '100%' }}
            />
          ) : (
            <InputNumber
              className="wide-input-number" // Apply the custom class
              ref={inputRef}
              min={0}
              step={0.01}
              onPressEnter={save}
              onBlur={save}
              style={{ width: '100%' }}
              formatter={(value) => `${value}`}
              parser={(value) => value}
              precision={2}
            />
          )}
        </Form.Item>
      </Form>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
      >
        {children || ' '}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableCell2 = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave2,
  ...restProps
}) => {
  const [editingValue, setEditingValue] = useState(null);

  // Debounce handler for the value change to avoid immediate rounding
  const debouncedSave = debounce((value) => {
    const roundedValue = roundFloat(value); // Round after debounce
    handleSave2(record.key, dataIndex, roundedValue);
  }, 300); // Adjust debounce delay (300ms here)

  const handleChange = (value) => {
    setEditingValue(value); // Temporarily hold the raw input before rounding
    debouncedSave(value);
  };

  useEffect(
    () =>
      // Clean up debounce on unmount
      () => {
        debouncedSave.cancel();
      },
    []
  );

  let childNode = children || '';

  if (editable && record) {
    childNode = (
      <InputNumber
        min={0}
        value={editingValue !== null ? editingValue : record[dataIndex]}
        onChange={handleChange}
        className="wide-input-number" // Apply the custom class
        step={0.01} // Allow smooth input for decimal numbers
        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
      />
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const App = () => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
  });

  const { socket, data_date } = useSelector((state) => ({
    socket: state.socket,
    data_date: state.data_date,
  }));
  const dispatch = useDispatch();

  const [dataSource, setDataSource] = useState([]);
  const [dataSource2, setDataSource2] = useState([]);
  const [dataSource3, setDataSource3] = useState([]);
  const [totalTable3, seTtotalTable3] = useState({});
  const [newRows, setNewRows] = useState([]); // Track newly added rows
  const [initialSummary, seTinitialSummary] = useState({});

  const get_data = (month) => {
    socket.on('request_out', ({ status, content }) => {
      console.log('🚀 ~ socket.on ~ content:', content);
      socket.removeAllListeners('request_out');
      if (status === '0') {
        setDataSource(content?.table1);
        setDataSource2(content?.table2);
        setDataSource3(content?.table3);
        seTtotalTable3(content?.total_t3);
        seTinitialSummary(content?.initial_summary);
      } else {
        newAlert({ msg: 'Системд алдаа гарлаа' });
      }
      dispatch(end_loading({}));
    });
    socket.emit('request_in', {
      channel: 'ts001_05_report',
      content: {
        action_type: 'get_sa19',
        month,
      },
    });
    dispatch(start_loading({}));
  };
  const handleSend = async () => {
    socket.on('request_out', ({ status, content }) => {
      socket.removeAllListeners('request_out');
      newAlert({
        type: status === '0' ? 'success' : 'error',
        msg: content.Message,
        onSuccess: [get_data(data_date.month), dispatch(disable_state({}))],
      });
      dispatch(end_loading({}));
    });
    const content = {
      month: data_date?.month,
      action_type: 'save_sa19',
      table_1: dataSource,
      table_2: dataSource2,
      table_3: dataSource3,
    };

    socket.emit('request_in', {
      channel: 'ts001_05_report',
      content,
    });
    dispatch(start_loading({}));
  };

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const handleConfirm = () => {
    socket.on('request_out', ({ status, content }) => {
      socket.removeAllListeners('request_out');
      newAlert({
        type: status === '0' ? 'success' : 'error',
        msg: content.Message,
        onSuccess: [get_data(data_date.month), dispatch(disable_state({}))],
      });
      dispatch(end_loading({}));
    });
    const content = {
      month: data_date?.month,
      action_type: 'save_sa19',
      table_1: dataSource,
      table_2: dataSource2,
      table_3: dataSource3,
    };

    socket.emit('request_in', {
      channel: 'ts001_05_report',
      content,
    });
    dispatch(start_loading({}));
  };
  // useEffect(() => {
  //   console.log(dataSource);

  //   const allFieldsFilled = dataSource.every(
  //     (row) =>
  //       row.turul !== '' &&
  //       row.eh_uld !== '' &&
  //       row.uls !== '' &&
  //       row.huvi !== '' &&
  //       row.gadaad !== '' &&
  //       row.dun
  //   );
  //   setIsSubmitDisabled(allFieldsFilled);
  // }, [dataSource]);

  useEffect(() => {
    get_data(data_date.month);
  }, [data_date.month]);

  const handleSave = (updatedRecord) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => updatedRecord.key === item.key);
    if (index >= 0) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...updatedRecord });
    } else {
      // Handle case for new rows if needed
      newData.push(updatedRecord);
    }
    setDataSource(newData);
  };
  const handleSave2 = (key, dataIndex, value) => {
    const newData = [...dataSource2];
    const index = newData.findIndex((item) => key === item.key);
    const item = newData[index];

    // Round the value before saving it
    const roundedValue = replaceFloat(value);
    console.log('Original value:', value);
    console.log('Rounded value:', roundedValue);
    // Update the data source with the rounded value
    newData.splice(index, 1, { ...item, [dataIndex]: roundedValue });

    setDataSource2(newData);
  };

  const handleSave3 = (row) => {
    setDataSource3((prevDataSource) =>
      prevDataSource.map((item) =>
        item.key === row.key ? { ...item, ...row } : item
      )
    );
    setNewRows((prevNewRows) =>
      prevNewRows.map((item) =>
        item.key === row.key ? { ...item, ...row } : item
      )
    ); // Update in newRows
  };
  // Helper function to compare floating-point numbers with tolerance
  const areNumbersEqual = (a, b, epsilon = 0.0001) => Math.abs(a - b) < epsilon;
  // Calculate the sums of only the newly added rows
  const newSummary = newRows.reduce(
    (acc, item) => {
      const firstBalance = parseFloat(item.omnoh_on) || 0;
      const etsUld = parseFloat(item.tailant_on) || 0;
      acc.omnoh_on += firstBalance;
      acc.tailant_on += etsUld;
      return acc;
    },
    { omnoh_on: 0, tailant_on: 0 }
  );

  // Use the initialSummary prop for confirmation logic
  const isConfirmDisabled =
    newRows.length > 0 &&
    (!areNumbersEqual(newSummary.omnoh_on, initialSummary.omnoh_on) ||
      !areNumbersEqual(newSummary.tailant_on, initialSummary.tailant_on));

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
  };

  const handleDelete = (key) => {
    setDataSource((prevDataSource) =>
      prevDataSource.filter((item) => item.key !== key)
    );
  };

  const handleDelete2 = (key) => {
    setDataSource3((prevDataSource) =>
      prevDataSource.filter((item) => item.key !== key)
    );
    setNewRows((prevNewRows) => prevNewRows.filter((item) => item.key !== key)); // Remove from newRows
  };

  const handleAddRow = () => {
    const newData = {
      key: (dataSource.length + 1).toString(),
      turul: '',
      omnoh_on: 0.0,
      tailant_on: 0.0,
      editable: {
        turul: true,
        omnoh_on: true,
        tailant_on: true,
      },
    };

    // Get the second-to-last index (before the last row)
    const insertPosition = dataSource.length - 1;

    // If there's at least one row, insert before the last row
    if (dataSource.length > 0) {
      const updatedData = [...dataSource];
      updatedData.splice(insertPosition, 0, newData); // Insert at second-to-last position
      setDataSource(updatedData);
    } else {
      // If the table is empty, just add the new row
      setDataSource([newData]);
    }

    dispatch(enable_state({}));
  };

  const handleAddRow2 = () => {
    const newData = {
      key: (dataSource3.length + 1).toString(),
      turul: '',
      omnoh_on: 0.0,
      tailant_on: 0.0,
      editable: {
        turul: true,
        omnoh_on: true,
        tailant_on: true,
      },
    };

    setDataSource3([...dataSource3, newData]);
    setNewRows([...newRows, newData]); // Keep track of the newly added row
    dispatch(enable_state({}));
  };

  const columns = [
    {
      title: 'Орлогын төрөл',
      dataIndex: 'turul',
      key: 'turul',
      width: 400,
      editable: true,
      render: (text, record) => text,
    },
    {
      title: 'Өмнөх оны дүн',
      dataIndex: 'omnoh_on',
      key: 'omnoh_on',
      editable: true,
      width: 200,
      render: (text, record) => {
        if (record.title) {
          return {
            children: null,
            props: {
              colSpan: 0,
            },
          };
        }
        return typeof text === 'number' ? roundFloat(text) : text;
      },
    },
    {
      title: 'Тайлант оны дүн',
      dataIndex: 'tailant_on',
      key: 'tailant_on',
      editable: true,
      width: 200,
      render: (text, record) => {
        if (record.title) {
          return {
            children: null,
            props: {
              colSpan: 0,
            },
          };
        }
        return typeof text === 'number' ? roundFloat(text) : text;
      },
    },
    {
      title: 'Устгах',
      key: 'action',
      width: 150,
      render: (_, record) => {
        const isKeyGreaterThanThree = record.key > 9;
        const isTurulNiiit =
          record.turul && record.turul.toLowerCase() === 'нийт дүн';

        if (isKeyGreaterThanThree && !isTurulNiiit) {
          return (
            <Tooltip title="Устгах">
              <DeleteOutlined
                onClick={() =>
                  customConfirm({
                    onOk: () => handleDelete(record.key),
                  })
                }
              />
            </Tooltip>
          );
        }
        return null;
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable && !col.children) {
      return col;
    }

    // If column has children, map over the child columns as well
    if (col.children) {
      return {
        ...col,
        children: col.children.map((childCol) => ({
          ...childCol,
          onCell: (record) => ({
            record,
            editable: record.editable && record.editable[childCol.dataIndex],
            dataIndex: childCol.dataIndex,
            title: childCol.title,
            handleSave,
          }),
        })),
      };
    }

    // Otherwise, handle non-nested columns
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: record.editable && record.editable[col.dataIndex],
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const columns2 = [
    {
      title: 'Төрөл',
      dataIndex: 'turul',
      key: 'turul',
      width: 400,
      align: 'left',
      render: (text, record) => text,
    },
    {
      title: 'Өмнөх оны дүн',
      dataIndex: 'omnoh_on',
      key: 'omnoh_on',
      editable: true,
      width: 200,
      render: (text, record) => {
        if (record.title) {
          return {
            children: null,
            props: {
              colSpan: 0,
            },
          };
        }
        return typeof text === 'number' ? roundFloat(text) : text;
      },
    },
    {
      title: 'Тайлант оны дүн',
      dataIndex: 'tailant_on',
      key: 'tailant_on',
      editable: true,
      width: 200,
      render: (text, record) => {
        if (record.title) {
          return {
            children: null,
            props: {
              colSpan: 0,
            },
          };
        }
        return typeof text === 'number' ? roundFloat(text) : text;
      },
    },
  ];

  const mergedColumns2 = columns2.map((col) => {
    if (!col.editable && !col.children) {
      return col;
    }
    if (col.children) {
      // Handle child columns
      return {
        ...col,
        children: col.children.map((childCol) => ({
          ...childCol,
          onCell: (record) => ({
            record,
            editable: record.editableFields
              ? record.editableFields[childCol.dataIndex]
              : false,
            dataIndex: childCol.dataIndex,
            title: childCol.title,
            handleSave2,
          }),
        })),
      };
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: record.editableFields
          ? record.editableFields[col.dataIndex]
          : false,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave2,
      }),
    };
  });

  const columns3 = [
    {
      title: 'Үзүүлэлт',
      dataIndex: 'turul',
      key: 'turul',
      width: 400,
      render: (text, record) => text,
      editable: true,
    },
    {
      title: 'Өмнөх оны дүн',
      dataIndex: 'omnoh_on',
      key: 'omnoh_on',
      editable: true,
      render: (text, record) => {
        if (record.title) {
          return {
            children: null,
            props: {
              colSpan: 0,
            },
          };
        }
        return typeof text === 'number' ? roundFloat(text) : text;
      },
    },
    {
      title: 'Тайлант оны дүн',
      dataIndex: 'tailant_on',
      key: 'tailant_on',
      editable: true,
      render: (text, record) => {
        if (record.title) {
          return {
            children: null,
            props: {
              colSpan: 0,
            },
          };
        }
        return typeof text === 'number' ? roundFloat(text) : text;
      },
    },
    {
      title: 'Устгах',
      key: 'action',
      render: (_, record) => {
        const isKeyGreaterThanThree = record.key > 6;
        const isTurulNiiit =
          record.turul && record.turul.toLowerCase() === 'нийт дүн';

        if (isKeyGreaterThanThree && !isTurulNiiit) {
          return (
            <Tooltip title="Устгах">
              <DeleteOutlined
                onClick={() =>
                  customConfirm({
                    onOk: () => handleDelete2(record.key),
                  })
                }
              />
            </Tooltip>
          );
        }
        return null;
      },
    },
  ];

  const mergedColumns3 = columns3.map((col) => {
    if (!col.editable && !col.children) {
      return col;
    }

    // If column has children, map over the child columns as well
    if (col.children) {
      return {
        ...col,
        children: col.children.map((childCol) => ({
          ...childCol,
          onCell: (record) => ({
            record,
            editable: record.editable && record.editable[childCol.dataIndex],
            dataIndex: childCol.dataIndex,
            title: childCol.title,
            handleSave3,
          }),
        })),
      };
    }

    // Otherwise, handle non-nested columns
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: record.editable && record.editable[col.dataIndex],
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave3,
      }),
    };
  });
  return (
    <div className="tax-account-table-container">
      <Collapse
        defaultActiveKey={['1', '2', '3']}
        expandIconPosition="right"
        bordered={false}
        expandIcon={({ isActive }) =>
          isActive ? <UpOutlined /> : <DownOutlined />
        }
      >
        <Panel header="19.1 Бусад орлого, олз(гарз), ашиг(алдагдал)" key="1">
          <div className="table-add-row">
            <div className="table-container">
              <Table
                components={{
                  body: {
                    cell: EditableCellAddRow,
                  },
                }}
                dataSource={dataSource}
                columns={mergedColumns}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="key"
                // summary={() => (
                //   <Table.Summary.Row>
                //     <Table.Summary.Cell index={0}>Нийт</Table.Summary.Cell>
                //     <Table.Summary.Cell index={1} />
                //     <Table.Summary.Cell index={2} />
                //     <Table.Summary.Cell index={3}>
                //       {roundFloat(totalSum)}
                //     </Table.Summary.Cell>
                //     <Table.Summary.Cell index={4} />
                //   </Table.Summary.Row>
                // )}
                footer={() => (
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      type="primary"
                      className="add-btn"
                      onClick={handleAddRow}
                    >
                      <PlusCircleOutlined />
                      Мөр нэмэх
                    </Button>
                    <Tooltip
                      title={
                        isSubmitDisabled
                          ? 'Нэмсэн мөрүүдийн дүн дээрх мөртэй таарах ёстой'
                          : ''
                      }
                    >
                      <Button
                        type="primary"
                        className="confirm-btn"
                        onClick={handleConfirm}
                        style={{ marginLeft: 8 }}
                        disabled={isSubmitDisabled}
                      >
                        Хадгалах
                      </Button>
                    </Tooltip>
                  </div>
                )}
              />
            </div>
          </div>
        </Panel>
        <Panel header="19.2 Гадаад валютын ханшийн зөрүүний олз, гарз" key="2">
          <Table
            className="tax-account-table-panel mw-1200"
            dataSource={dataSource2}
            columns={mergedColumns2}
            rowClassName={() => 'editable-row'}
            bordered
            pagination={false}
            components={{
              body: {
                cell: EditableCell2,
              },
            }}
            footer={() => (
              <Button
                type="primary"
                className="tax-account-button-unique-mini"
                onClick={() => handleSend(1)}
              >
                Хадгалах
              </Button>
            )}
          />
        </Panel>
        <Panel header="19.3 Бусад ашиг / алдагдал" key="3">
          <div className="table-add-row">
            <div className="table-container">
              <Table
                components={{
                  body: {
                    cell: EditableCellAddRow2,
                  },
                }}
                dataSource={dataSource3}
                columns={mergedColumns3}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="key"
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>Нийт</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      {totalTable3?.omnoh_on}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      {totalTable3?.tailant_on}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
                footer={() => (
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      type="primary"
                      className="add-btn"
                      onClick={handleAddRow2}
                    >
                      <PlusCircleOutlined />
                      Мөр нэмэх
                    </Button>
                    <Tooltip
                      title={
                        isConfirmDisabled
                          ? 'Нэмсэн мөрүүдийн дүн дээрх мөртэй таарах ёстой'
                          : ''
                      }
                    >
                      <Button
                        type="primary"
                        className="confirm-btn"
                        onClick={handleConfirm}
                        style={{ marginLeft: 8 }}
                        disabled={isConfirmDisabled}
                      >
                        Хадгалах
                      </Button>
                    </Tooltip>
                  </div>
                )}
              />
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

export default App;
